import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, Lock, Globe, UserCircle } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { LogoIcon } from "./LogoWithText";
import { useLanguage } from "@/context/LanguageContext";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#productos", label: t("nav_products") },
    { href: "#servicios", label: t("nav_services") },
    { href: "#sectores", label: t("nav_sectors") },
    { href: "#precios", label: t("nav_pricing") },
    { href: "#testimonios", label: t("nav_testimonials") },
    { href: "/blog", label: t("nav_blog"), isRoute: true },
    { href: "#contacto", label: t("nav_contact") },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith("#")) {
      if (!isHomePage) {
        window.location.href = "/" + href;
        return;
      }
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#02040a]/90 backdrop-blur-lg border-b border-white/5"
          : "bg-transparent"
      }`}
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#"
            data-testid="nav-logo"
            className="flex items-center gap-3"
            onClick={(e) => {
              e.preventDefault();
              if (isHomePage) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.location.href = "/";
              }
            }}
          >
            <LogoIcon size={44} />
            <div className="flex flex-col">
              <span className="text-white font-bold tracking-wide text-lg leading-tight">
                GLOBAL <span className="text-[#3b82f6]">METAFORA</span>
              </span>
              <span className="text-[#3b82f6]/70 text-[10px] font-semibold tracking-[0.3em] uppercase leading-tight">APP</span>
            </div>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-link-blog`}
                  className="nav-link text-sm font-medium uppercase tracking-wider"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.href}
                  data-testid={`nav-link-${link.href.replace("#", "")}`}
                  onClick={() => scrollToSection(link.href)}
                  className="nav-link text-sm font-medium uppercase tracking-wider"
                >
                  {link.label}
                </button>
              )
            ))}
          </div>

          {/* CTA Button + Admin + Lang */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLang}
              data-testid="lang-toggle"
              className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#475569] hover:text-[#06b6d4] hover:border-[#06b6d4]/30 transition-all text-xs font-bold"
              title={lang === "es" ? "Switch to English" : "Cambiar a Español"}
            >
              {lang === "es" ? "EN" : "ES"}
            </button>
            <Link
              to="/admin"
              data-testid="nav-admin-link"
              className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#475569] hover:text-[#3b82f6] hover:border-[#3b82f6]/30 transition-all"
              title="Panel de Admin"
            >
              <Lock size={14} />
            </Link>
            <Link
              to="/portal"
              data-testid="nav-portal-link"
              className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#475569] hover:text-[#06b6d4] hover:border-[#06b6d4]/30 transition-all"
              title="Mi Portal"
            >
              <UserCircle size={16} />
            </Link>
            <button
              data-testid="nav-cta"
              onClick={() => scrollToSection("#precios")}
              className="btn-primary"
            >
              <span>{t("nav_plans")}</span>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[#0b1221] border-t border-white/5"
          >
            <div className="py-4 space-y-2">
              {navLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    data-testid={`mobile-nav-blog`}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-left px-4 py-3 text-[#94a3b8] hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    data-testid={`mobile-nav-${link.href.replace("#", "")}`}
                    onClick={() => scrollToSection(link.href)}
                    className="block w-full text-left px-4 py-3 text-[#94a3b8] hover:text-white hover:bg-white/5 transition-colors"
                  >
                    {link.label}
                  </button>
                )
              ))}
              <div className="px-4 pt-2 flex gap-2">
                <button
                  onClick={toggleLang}
                  data-testid="mobile-lang-toggle"
                  className="flex items-center justify-center gap-1 py-3 px-4 rounded-lg border border-white/10 text-[#94a3b8] hover:text-[#06b6d4] hover:border-[#06b6d4]/30 transition-all text-xs font-bold"
                >
                  {lang === "es" ? "EN" : "ES"}
                </button>
                <Link
                  to="/admin"
                  data-testid="mobile-nav-admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-white/10 text-[#94a3b8] hover:text-[#3b82f6] hover:border-[#3b82f6]/30 transition-all"
                >
                  <Lock size={14} />
                </Link>
                <Link
                  to="/portal"
                  data-testid="mobile-nav-portal"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="flex items-center justify-center gap-2 py-3 px-4 rounded-lg border border-white/10 text-[#94a3b8] hover:text-[#06b6d4] hover:border-[#06b6d4]/30 transition-all"
                >
                  <UserCircle size={16} />
                </Link>
                <button
                  data-testid="mobile-nav-cta"
                  onClick={() => scrollToSection("#precios")}
                  className="btn-primary flex-1"
                >
                  <span>{t("nav_plans")}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
};

export default Navbar;
